import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Input, InputNumber, Select, Space, Image,
  Popconfirm, message, Typography, Empty,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined,
} from '@ant-design/icons';
import { familiarService, API_HOST } from '../services/api';

const { Text } = Typography;

/**
 * Mini-grilla INLINE de familiares vinculados a un animal.
 *
 * Se renderiza dentro de la ficha del animal (AnimalDetalle.jsx). Permite:
 *  - Ver el listado de vinculaciones con la foto del familiar del catalogo
 *  - Editar inline 'vinculo_familiar' (texto libre) y 'orden_vista'
 *    con save-on-blur (solo dispara PUT si el valor cambio)
 *  - Eliminar una vinculacion (NO borra el familiar del catalogo)
 *  - Agregar una nueva vinculacion con autocomplete sobre el catalogo
 *    + campos vinculo/orden inline; falla con codigo VINCULACION_DUPLICADA
 *    si ya existe esa pareja animal-familiar.
 *
 * Props:
 *   animalId: number  — id del animal cuya ficha se esta viendo
 */
const FamiliaresVinculados = ({ animalId }) => {
  const [vinculaciones, setVinculaciones]   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [catalogo, setCatalogo]             = useState([]);
  const [catalogoLoading, setCatalogoLoading] = useState(false);
  const [savingRowId, setSavingRowId]       = useState(null);

  // Estado del row 'agregar nuevo' al final de la grilla
  const [newFamiliarId, setNewFamiliarId]   = useState(null);
  const [newVinculo, setNewVinculo]         = useState('');
  const [newOrden, setNewOrden]             = useState(null);
  const [adding, setAdding]                 = useState(false);

  const fetchVinculaciones = useCallback(async () => {
    if (!animalId) return;
    setLoading(true);
    try {
      const res = await familiarService.getVinculacionesByAnimal(animalId);
      setVinculaciones(res.data.familiares || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar familiares');
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  // El catalogo se carga una vez para el autocomplete del row 'agregar'.
  // 500 entries cubre cabanas normales; si crece se puede pasar a search server-side.
  const fetchCatalogo = useCallback(async () => {
    setCatalogoLoading(true);
    try {
      const res = await familiarService.getAll({ per_page: 500 });
      setCatalogo(res.data.familiares || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar catalogo');
    } finally {
      setCatalogoLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVinculaciones();
    fetchCatalogo();
  }, [fetchVinculaciones, fetchCatalogo]);

  // Save-on-blur: solo dispara PUT si el valor en pantalla difiere del
  // que vino del servidor. Optimistic update tras exito.
  const saveField = async (idAf, field, newValue) => {
    const fila = vinculaciones.find(v => v.id_animal_familiar === idAf);
    if (!fila) return;
    const currentValue = fila[field];
    // Normalizar undefined/'' a null para la comparacion
    const norm = (v) => (v === '' || v === undefined ? null : v);
    if (norm(currentValue) === norm(newValue)) return;
    setSavingRowId(idAf);
    try {
      await familiarService.actualizarVinculacion(animalId, idAf, { [field]: newValue });
      setVinculaciones(prev => prev.map(v =>
        v.id_animal_familiar === idAf ? { ...v, [field]: newValue } : v
      ));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar');
      // Revertir visualmente refrescando
      fetchVinculaciones();
    } finally {
      setSavingRowId(null);
    }
  };

  const handleDesvincular = async (idAf) => {
    try {
      await familiarService.desvincular(animalId, idAf);
      setVinculaciones(prev => prev.filter(v => v.id_animal_familiar !== idAf));
      message.success('Familiar desvinculado');
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al desvincular');
    }
  };

  const resetNewRow = () => {
    setNewFamiliarId(null);
    setNewVinculo('');
    setNewOrden(null);
  };

  const handleAgregar = async () => {
    if (!newFamiliarId) {
      message.warning('Elegi un familiar del catalogo');
      return;
    }
    setAdding(true);
    try {
      const payload = {
        id_familiar: newFamiliarId,
        vinculo_familiar: newVinculo.trim() || undefined,
        orden_vista: newOrden ?? undefined,
      };
      const res = await familiarService.vincular(animalId, payload);
      setVinculaciones(prev => [...prev, res.data.vinculacion]);
      resetNewRow();
      message.success('Familiar vinculado');
    } catch (error) {
      const data = error.response?.data;
      if (data?.codigo === 'VINCULACION_DUPLICADA') {
        message.warning('Ese familiar ya esta vinculado a este animal');
      } else {
        message.error(data?.error || 'Error al vincular');
      }
    } finally {
      setAdding(false);
    }
  };

  // Excluye del autocomplete los familiares que ya estan vinculados
  const yaVinculadosIds = new Set(vinculaciones.map(v => v.id_familiar));
  const opcionesCatalogo = catalogo
    .filter(f => !yaVinculadosIds.has(f.id_familiar))
    .map(f => ({ value: f.id_familiar, label: `${f.rp_familiar} — ${f.nom_familiar}` }));

  const columns = [
    {
      title: 'Foto',
      dataIndex: 'url_foto_familiar',
      key: 'foto',
      width: 70,
      render: (url) => url ? (
        <Image src={`${API_HOST}${url}`} width={40} height={40}
               style={{ objectFit: 'cover', borderRadius: 4 }} />
      ) : (
        <div style={{
          width: 40, height: 40, background: '#f0f0f0', borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#bbb', fontSize: 10,
        }}>—</div>
      ),
    },
    {
      title: 'RP',
      dataIndex: 'rp_familiar',
      key: 'rp_familiar',
      width: 130,
      render: (rp) => <code>{rp}</code>,
    },
    {
      title: 'Nombre',
      dataIndex: 'nom_familiar',
      key: 'nom_familiar',
    },
    {
      title: 'Vinculo',
      dataIndex: 'vinculo_familiar',
      key: 'vinculo_familiar',
      width: 200,
      render: (value, record) => (
        <Input
          size="small"
          defaultValue={value || ''}
          placeholder="Madre, padre, media hna..."
          disabled={savingRowId === record.id_animal_familiar}
          onBlur={(e) => saveField(record.id_animal_familiar, 'vinculo_familiar', e.target.value.trim() || null)}
        />
      ),
    },
    {
      title: 'Orden',
      dataIndex: 'orden_vista',
      key: 'orden_vista',
      width: 90,
      align: 'center',
      render: (value, record) => (
        <InputNumber
          size="small"
          min={0}
          max={999}
          defaultValue={value ?? undefined}
          placeholder="—"
          style={{ width: 70 }}
          disabled={savingRowId === record.id_animal_familiar}
          onBlur={(e) => {
            const raw = e.target.value;
            const num = raw === '' ? null : Number(raw);
            saveField(record.id_animal_familiar, 'orden_vista', num);
          }}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'right',
      render: (_, record) => (
        <Popconfirm
          title="¿Desvincular este familiar?"
          description="El familiar sigue en el catalogo; solo se quita de este animal."
          onConfirm={() => handleDesvincular(record.id_animal_familiar)}
          okText="Si"
          cancelText="No"
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={vinculaciones}
        rowKey="id_animal_familiar"
        loading={loading}
        pagination={false}
        size="small"
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Sin familiares vinculados. Agrega uno abajo." />,
        }}
      />

      {/* Row 'agregar nuevo' inline */}
      <div style={{
        marginTop: 16, padding: 12, background: '#fafafa',
        border: '1px dashed #d9d9d9', borderRadius: 6,
      }}>
        <Text strong style={{ marginRight: 8 }}>
          <PlusOutlined /> Vincular nuevo familiar:
        </Text>
        <Space wrap style={{ marginTop: 8 }} size="middle">
          <Select
            showSearch
            placeholder="Buscar en el catalogo (RP o nombre)"
            style={{ minWidth: 320 }}
            value={newFamiliarId}
            onChange={setNewFamiliarId}
            options={opcionesCatalogo}
            loading={catalogoLoading}
            optionFilterProp="label"
            allowClear
            notFoundContent={
              catalogoLoading ? 'Cargando...' :
              opcionesCatalogo.length === 0 ? 'Todos los familiares del catalogo ya estan vinculados' :
              'Sin resultados'
            }
          />
          <Input
            placeholder="Vinculo (madre, padre, etc.)"
            value={newVinculo}
            onChange={(e) => setNewVinculo(e.target.value)}
            style={{ width: 240 }}
          />
          <InputNumber
            placeholder="Orden"
            min={0}
            max={999}
            value={newOrden}
            onChange={setNewOrden}
            style={{ width: 100 }}
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleAgregar}
            loading={adding}
            disabled={!newFamiliarId}
          >
            Vincular
          </Button>
          {(newFamiliarId || newVinculo || newOrden !== null) && (
            <Button onClick={resetNewRow} disabled={adding}>
              Cancelar
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

export default FamiliaresVinculados;
